import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import ListAltIcon from '@mui/icons-material/ListAlt'
import {
  Alert,
  Avatar,
  Backdrop,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  CssBaseline,
  Tab,
  Tabs,
  Typography
} from '@mui/material'
import React from 'react'
import { Navigate } from 'react-router-dom'

import { MyBookingsList } from '../components/booking'
import BookingViewToggle, {
  BookingView
} from '../components/booking/BookingViewToggle'
import TeamBookingsList from '../components/booking/TeamBookingsList'
import { CalendarWeek, OfficeFilter } from '../components/calendar'
import {
  AppBarR as PersistentDrawerRight,
  CalendarControls,
  Wrapper
} from '../components/common'
import CalendarPageHint from '../components/onboarding/CalendarPageHint'
import { useHome } from '../hooks/useHome'
import { usePageVisitTracker } from '../hooks/usePageVisitTracker'
import { AnalyticsService } from '../services/AnalyticsService'
import { loadTeamBookings } from '../state'
import { AnalyticsEvents } from '../types/AnalyticsEvents'
import Database from '../types/Database'
import { getPreviousOrCurrentSunday } from '../utils/calendarHelpers'

/**
 * Home - Main home page component
 *
 * Displays the office calendar with date controls and booking information.
 * Includes tabs for Calendar view and My Bookings list view.
 * Redirects to landing page for unauthenticated users.
 */
const Home = () => {
  console.log('📅 Home/Calendar page loaded')
  const { userState, handleFromDayChange, setNewAmountValue, refreshBookings } =
    useHome()
  const [tabValue, setTabValue] = React.useState(0)
  const [bookingView, setBookingView] = React.useState<BookingView>('my')
  const [teamBookings, setTeamBookings] = React.useState<
    Database.EnrichedTeamBooking[]
  >([])
  const [loadingTeamBookings, setLoadingTeamBookings] = React.useState(false)
  const [teamBookingsError, setTeamBookingsError] = React.useState<
    string | null
  >(null)
  const [capacityWarning, setCapacityWarning] = React.useState<string>('')

  // Initialize selectedOfficeIds from localStorage if available
  const [selectedOfficeIds, setSelectedOfficeIds] = React.useState<string[]>(
    () => {
      // We don't have userState yet, so we'll load it in the effect
      return []
    }
  )

  const lastLoadedUserId = React.useRef<string | null>(null)
  const {
    hasVisited,
    markVisited,
    isLoading: isLoadingPageVisit
  } = usePageVisitTracker('calendar')
  const [showHint, setShowHint] = React.useState(false)

  // Show hint if user hasn't visited calendar page before
  React.useEffect(() => {
    if (!isLoadingPageVisit && !hasVisited && userState.user) {
      setShowHint(true)
    }
  }, [isLoadingPageVisit, hasVisited, userState.user])

  // Handle hint dismissal
  const handleHintDismiss = () => {
    setShowHint(false)
    markVisited()
  }

  // Dismiss hint when user clicks on calendar date
  const handleCalendarDateClick = () => {
    if (showHint) {
      handleHintDismiss()
    }
  }

  // Load filter state from localStorage on mount or when user/offices change
  React.useEffect(() => {
    if (
      userState.user?.uid &&
      userState.offices &&
      userState.offices.length > 0
    ) {
      const storageKey = `officeFilter_${userState.user.uid}`
      const sessionKey = `officeFilterLoaded_${userState.user.uid}`

      // Check if we've already loaded for this user in this session
      const alreadyLoaded = sessionStorage.getItem(sessionKey)

      // Only load once per user per browser session
      if (!alreadyLoaded && lastLoadedUserId.current !== userState.user.uid) {
        const stored = localStorage.getItem(storageKey)

        if (stored) {
          try {
            const parsed = JSON.parse(stored)
            if (
              parsed.selectedOfficeIds &&
              Array.isArray(parsed.selectedOfficeIds) &&
              parsed.selectedOfficeIds.length > 0
            ) {
              // Validate that stored offices still exist
              const validIds = parsed.selectedOfficeIds.filter((id: string) =>
                userState.offices?.some(o => o.oid === id)
              )
              if (validIds.length > 0) {
                setSelectedOfficeIds(validIds)
              }
            }
          } catch (error) {
            console.error(
              'Failed to parse office filter from localStorage:',
              error
            )
            // Clear corrupted data
            try {
              localStorage.removeItem(storageKey)
            } catch {}
          }
        }
        lastLoadedUserId.current = userState.user.uid
        sessionStorage.setItem(sessionKey, 'true')
      }
    }
  }, [userState.user?.uid, userState.offices])

  // Save filter state to localStorage on change
  React.useEffect(() => {
    if (
      userState.user?.uid &&
      lastLoadedUserId.current === userState.user.uid
    ) {
      const storageKey = `officeFilter_${userState.user.uid}`
      try {
        localStorage.setItem(
          storageKey,
          JSON.stringify({
            selectedOfficeIds,
            version: 1
          })
        )
      } catch (error) {
        console.error('Failed to save office filter to localStorage:', error)
        // Continue without persistence
      }
    }
  }, [selectedOfficeIds, userState.user?.uid])

  // Check for pending capacity warning from signup
  React.useEffect(() => {
    const warning = localStorage.getItem('pendingCapacityWarning')
    if (warning) {
      console.log('📢 Displaying pending capacity warning:', warning)
      setCapacityWarning(warning)
      // Set a persistent flag that user was rejected due to capacity
      // This prevents onboarding wizard from showing on subsequent page navigations
      localStorage.setItem('wasRejectedDueToCapacity', 'true')
      localStorage.removeItem('pendingCapacityWarning')
    }
  }, [])

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue)
  }

  const handleViewChange = async (view: BookingView) => {
    setBookingView(view)
    if (view === 'team' && userState.user?.uid) {
      setLoadingTeamBookings(true)
      setTeamBookingsError(null)
      try {
        const bookings = await loadTeamBookings(userState.user.uid)
        setTeamBookings(bookings)

        // Track team bookings viewed event
        AnalyticsService.trackEvent(AnalyticsEvents.TEAM_BOOKINGS_VIEWED, {})
      } catch (error) {
        console.error('Error loading team bookings:', error)
        const errorMessage =
          error instanceof Error
            ? error.message
            : 'Unable to load team bookings. Please check your connection and try again.'
        setTeamBookingsError(errorMessage)
      } finally {
        setLoadingTeamBookings(false)
      }
    }
  }

  const handleTeamBookingDeleted = async () => {
    if (userState.user?.uid) {
      setLoadingTeamBookings(true)
      setTeamBookingsError(null)
      try {
        const bookings = await loadTeamBookings(userState.user.uid)
        setTeamBookings(bookings)
      } catch (error) {
        console.error('Error reloading team bookings:', error)
        const errorMessage =
          error instanceof Error
            ? error.message
            : 'Unable to reload team bookings. Please try again.'
        setTeamBookingsError(errorMessage)
      } finally {
        setLoadingTeamBookings(false)
      }
    }
  }

  const handleRetryLoadTeamBookings = () => {
    handleViewChange('team')
  }

  // Filter bookings based on selected offices
  const filteredBookings = React.useMemo(() => {
    if (selectedOfficeIds.length === 0) {
      return userState.bookings ?? [] // Show all if no filter
    }
    return (userState.bookings ?? []).filter(booking =>
      selectedOfficeIds.includes(booking.oid ?? '')
    )
  }, [userState.bookings, selectedOfficeIds])

  // Show loading while authenticating or loading initial data
  if (userState.isAuthenticating || userState.isLoadingData) {
    return (
      <Backdrop
        sx={{
          color: theme => (theme.palette.mode === 'dark' ? '#fff' : '#000'),
          zIndex: 9999,
          position: 'fixed',
          backgroundColor: theme =>
            theme.palette.mode === 'dark'
              ? 'rgba(0, 0, 0, 0.95)'
              : 'rgba(255, 255, 255, 0.95)'
        }}
        open={true}
      >
        <CircularProgress color='inherit' size={60} />
      </Backdrop>
    )
  }

  // If no user, redirect to landing page
  if (userState.user === null) {
    return <Navigate to='/' replace />
  }

  return (
    <>
      <PersistentDrawerRight />
      <Wrapper marginTop={1}>
        {capacityWarning && (
          <Alert
            severity='warning'
            onClose={() => setCapacityWarning('')}
            sx={{ mx: 2, mt: 2, mb: 1 }}
          >
            {capacityWarning}
          </Alert>
        )}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            px: 2,
            pt: 1
          }}
        >
          <CssBaseline />

          {/* Header Card with Title and Tabs */}
          <Card sx={{ mb: 3, width: '100%', maxWidth: 1600 }}>
            <CardContent>
              <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}
              >
                <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
                  <CalendarMonthIcon fontSize='small' />
                </Avatar>
                <Typography variant='h6' sx={{ fontWeight: 600 }}>
                  Calendar
                </Typography>
              </Box>

              <Typography variant='body1' color='text.secondary' sx={{ mb: 2 }}>
                View and manage desk bookings across your offices
              </Typography>

              {/* Tabs */}
              <Tabs
                value={tabValue}
                onChange={handleTabChange}
                aria-label='calendar tabs'
                sx={{ borderBottom: 1, borderColor: 'divider' }}
              >
                <Tab
                  icon={<CalendarMonthIcon />}
                  iconPosition='start'
                  label='Calendar'
                  id='tab-0'
                  aria-controls='tabpanel-0'
                />
                <Tab
                  icon={<ListAltIcon />}
                  iconPosition='start'
                  label='My Bookings'
                  id='tab-1'
                  aria-controls='tabpanel-1'
                />
              </Tabs>
            </CardContent>
          </Card>

          {/* Tab Panel 0: Calendar View */}
          {tabValue === 0 && (
            <>
              {/* Calendar Page Hint */}
              {showHint && (
                <Box sx={{ mb: 2, width: '100%', maxWidth: 1600 }}>
                  <CalendarPageHint
                    visible={showHint}
                    onDismiss={handleHintDismiss}
                  />
                </Box>
              )}

              {/* Calendar Controls and Legend */}
              <Card sx={{ mb: 3, width: '100%', maxWidth: 1600 }}>
                <CardContent>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: { xs: 'column', md: 'row' },
                      gap: 3,
                      alignItems: 'stretch'
                    }}
                  >
                    <Box sx={{ flex: { xs: 'none', md: 1 } }}>
                      <CalendarControls
                        selectedFromDate={userState.selectedFromDate}
                        selectedRecurAmount={userState.selectedRecurAmount}
                        onFromDateChange={handleFromDayChange}
                        onAmountChange={setNewAmountValue}
                      />
                    </Box>

                    <Box sx={{ flex: { xs: 'none', md: 1 } }}>
                      <OfficeFilter
                        offices={userState.offices || []}
                        selectedOfficeIds={selectedOfficeIds}
                        onFilterChange={setSelectedOfficeIds}
                      />
                    </Box>
                  </Box>
                </CardContent>
              </Card>

              {/* Calendar Week Box */}
              <Box
                sx={{
                  width: '100%',
                  maxWidth: 1600,
                  borderRadius: 3,
                  boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
                  border: '1px solid',
                  borderColor: 'divider',
                  backdropFilter: 'blur(10px)',
                  p: 3
                }}
                onClick={handleCalendarDateClick}
              >
                <CalendarWeek
                  bookings={filteredBookings}
                  uid={userState.user?.uid}
                  startDate={getPreviousOrCurrentSunday(
                    new Date(userState.selectedFromDate)
                  )}
                  numOfDays={userState.selectedRecurAmount}
                  deleteBooking={null}
                />
              </Box>
            </>
          )}

          {/* Tab Panel 1: My Bookings List */}
          {tabValue === 1 && (
            <Box
              sx={{
                width: '100%',
                maxWidth: 1600
              }}
            >
              {/* Booking View Toggle */}
              <Box sx={{ mb: 2 }}>
                <BookingViewToggle
                  view={bookingView}
                  onViewChange={handleViewChange}
                />
              </Box>

              {/* Conditional Rendering Based on View */}
              {bookingView === 'my' ? (
                <MyBookingsList
                  bookings={userState.userBookings || []}
                  onBookingDeleted={refreshBookings}
                  currentUserId={userState.user?.uid || ''}
                />
              ) : loadingTeamBookings ? (
                <Card sx={{ textAlign: 'center', py: 6 }}>
                  <CardContent>
                    <CircularProgress />
                    <Typography
                      variant='body2'
                      color='text.secondary'
                      sx={{ mt: 2 }}
                    >
                      Loading team bookings...
                    </Typography>
                  </CardContent>
                </Card>
              ) : teamBookingsError ? (
                <Card sx={{ textAlign: 'center', py: 6 }}>
                  <CardContent>
                    <Alert severity='error' sx={{ mb: 2 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          mb: 1
                        }}
                      >
                        <ErrorOutlineIcon fontSize='small' />
                        <Typography variant='body2' sx={{ fontWeight: 600 }}>
                          {teamBookingsError}
                        </Typography>
                      </Box>
                      <Typography variant='caption' color='text.secondary'>
                        Check your internet connection and try again.
                      </Typography>
                    </Alert>
                    <Button
                      variant='contained'
                      onClick={handleRetryLoadTeamBookings}
                    >
                      Retry
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <TeamBookingsList
                  teamBookings={teamBookings}
                  onBookingDeleted={handleTeamBookingDeleted}
                  currentUserId={userState.user?.uid || ''}
                />
              )}
            </Box>
          )}
        </Box>
      </Wrapper>
    </>
  )
}

export default Home
